import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Car, Receipt, Hotel, Clock, ChevronLeft, ChevronRight, Save, Lock, Trash2, ExternalLink, Plus, Undo2 } from 'lucide-react';
import {
    createAllowanceExpense,
    deleteExpenseV2,
    getExpenseTypes,
    getExpensesV2,
    updateAllowanceExpense,
} from '../lib/api';
import { hapticSave } from '../lib/haptics';
import { cn } from '../lib/utils';

// Skatteverket international per-diem ("utlandstraktamente") rate table.
const SKV_INTL_RATE_URL = 'https://www.skatteverket.se/privat/skatter/arbeteochinkomst/traktamente/utlandstraktamente.4.2b543913a42158acf800016035.html';

// The international rate URL is admin-configurable, so it is untrusted input.
// Only allow http(s) — a javascript:/data: value would be an href XSS vector.
// Falls back to the built-in Skatteverket link for anything invalid.
const safeHttpUrl = (url, fallback) => {
    try {
        const parsed = new URL((url || '').trim());
        return (parsed.protocol === 'https:' || parsed.protocol === 'http:') ? parsed.href : fallback;
    } catch {
        return fallback;
    }
};
import AllowanceRecentEntries from './allowance-recent-entries';
import DayTimeEntriesForm from './day-time-entries-form';
import DayMileageForm from './day-mileage-form';
import DayReceiptForm from './day-receipt-form';

// Vehicle / allowance type options are fetched dynamically from GET /expense-types
// (see the `expenseTypes` state below) so this dialog can never silently drift
// from backend/config/expense_config.py.
// FALLBACK_EXPENSE_TYPES is used ONLY when that fetch fails: without it a network
// error would leave the mileage/allowance dropdowns permanently disabled on a
// "loading" placeholder, bricking a core form. The dynamic fetch always wins when
// it succeeds; the fallback is a last resort that keeps the form usable offline
// (it may miss a newly-added backend type until the fetch recovers — strictly
// better than an unusable form). (RECEIPT_TYPES was previously declared here but
// never rendered by this file — DayReceiptForm owns its own receipt-type list.)
const FALLBACK_EXPENSE_TYPES = {
    vehicle_types: [
        { id: 'own_car', name: 'Own Car' },
        { id: 'company_car', name: 'Company Car' },
        { id: 'motorcycle', name: 'Motorcycle' },
        { id: 'electric_car', name: 'Electric Car' },
        { id: 'other', name: 'Other' },
    ],
    allowance_types: [
        { id: 'domestic_day', name: 'Domestic Day Allowance' },
        { id: 'domestic_half_day', name: 'Domestic Half Day' },
        { id: 'domestic_night', name: 'Domestic Night Allowance' },
        { id: 'international_day', name: 'International Day Allowance' },
        { id: 'international_night', name: 'International Night Allowance' },
    ],
};


// ── Allowance tab: multi-entry row helpers (mirrors DayMileageForm/DayReceiptForm) ─
// The month-summary payload (existingExpenses, from GET /calendar/month-summary)
// only carries a thin projection for allowances (id/date/type/destination/amount) —
// enough to render the "for this day" list, but NOT enough to safely seed an edit
// form (missing country/start_datetime/end_datetime/nights/meals). Editing a row
// seeded only from the thin projection would silently overwrite those fields with
// defaults on Save — a Principle 2 (No Data Loss) violation. So each row also
// carries `_detailsLoaded`: false until an enrichment fetch (GET /expenses/v2)
// fills in the full record; the edit fieldset stays disabled until then (delete
// still works immediately — it only needs the id).
const ALLOWANCE_ROW_FIELDS = [
    'allowance_type', 'destination', 'country', 'start_datetime', 'end_datetime',
    'nights', 'breakfast_provided', 'lunch_provided', 'dinner_provided',
    'international_day_rate', 'international_night_rate', 'project_id', 'activity_id', 'billable', 'notes',
];

const ALLOWANCE_ROW_DEFAULT_TYPE = 'domestic_day';
const ALLOWANCE_ROW_DEFAULT_COUNTRY = 'Sweden';

// Single source of truth for "has the user put anything meaningful into this
// row" — used consistently by the dirty check, validation, and the save
// handler's empty-row skip so the three can never drift out of sync with each
// other (a row recognized as dirty must also be recognized as saveable).
function allowanceRowHasData(row, date) {
    const defaultStart = date ? `${date}T08:00` : undefined;
    const defaultEnd = date ? `${date}T17:00` : undefined;
    return !!(
        row.destination ||
        row.notes ||
        row.project_id ||
        row.activity_id ||
        (row.nights > 0) ||
        (row.breakfast_provided > 0) ||
        (row.lunch_provided > 0) ||
        (row.dinner_provided > 0) ||
        (row.international_day_rate !== '' && row.international_day_rate != null) ||
        (row.international_night_rate !== '' && row.international_night_rate != null) ||
        (row.allowance_type && row.allowance_type !== ALLOWANCE_ROW_DEFAULT_TYPE) ||
        (row.country && row.country !== ALLOWANCE_ROW_DEFAULT_COUNTRY) ||
        (defaultStart && row.start_datetime && row.start_datetime !== defaultStart) ||
        (defaultEnd && row.end_datetime && row.end_datetime !== defaultEnd)
    );
}

// Allowance rows belong to exactly one calendar day (this tab only ever shows
// entries for `effectiveDate`) but there's no separate `date` field on the
// row to reconcile against a datetime edit — so the start/end datetime-local
// inputs only let the user change the time-of-day; the date portion is
// always pinned back to the row's day, keeping the edited datetime and the
// (unsent-on-update, server-anchored) `date` field from drifting apart.
function withRowDate(datetimeLocalValue, rowDate) {
    const timePart = (datetimeLocalValue || '').split('T')[1] || '00:00';
    return `${rowDate}T${timePart}`;
}

function seedAllowanceRow(exp, date) {
    return {
        id: exp.id,
        allowance_type: exp.allowance_type || ALLOWANCE_ROW_DEFAULT_TYPE,
        destination: exp.destination || '',
        country: exp.country || ALLOWANCE_ROW_DEFAULT_COUNTRY,
        start_datetime: exp.start_datetime || `${date}T08:00`,
        end_datetime: exp.end_datetime || `${date}T17:00`,
        nights: exp.nights ?? 0,
        breakfast_provided: exp.breakfast_provided ?? 0,
        lunch_provided: exp.lunch_provided ?? 0,
        dinner_provided: exp.dinner_provided ?? 0,
        international_day_rate: exp.international_day_rate != null ? String(exp.international_day_rate) : '',
        international_night_rate: exp.international_night_rate != null ? String(exp.international_night_rate) : '',
        payment_method: exp.payment_method || 'personal_card',
        project_id: exp.project_id || '',
        // Phase 23 (PRD 12.1): activity_id is the join key, activity_name the
        // snapshot resolved server-side — never re-derived from it on read.
        activity_id: exp.activity_id || '',
        activity_name: exp.activity_name || '',
        billable: exp.billable ?? false,
        notes: exp.notes || '',
        amount: exp.amount != null ? exp.amount : null,
        _markDeleted: false,
        _detailsLoaded: !!exp._detailsLoaded,
    };
}

function emptyAllowanceRow(date) {
    return {
        id: undefined,
        allowance_type: ALLOWANCE_ROW_DEFAULT_TYPE,
        destination: '',
        country: ALLOWANCE_ROW_DEFAULT_COUNTRY,
        start_datetime: `${date}T08:00`,
        end_datetime: `${date}T17:00`,
        nights: 0,
        breakfast_provided: 0,
        lunch_provided: 0,
        dinner_provided: 0,
        international_day_rate: '',
        international_night_rate: '',
        payment_method: 'personal_card',
        project_id: '',
        activity_id: '',
        activity_name: '',
        billable: false,
        notes: '',
        amount: null,
        _markDeleted: false,
        _detailsLoaded: true,
    };
}

function hasAllowanceRowChanged(current, original) {
    return ALLOWANCE_ROW_FIELDS.some((f) => String(current[f] ?? '') !== String(original[f] ?? ''));
}

function extractAllowanceCreatePayload(row, date) {
    const isIntl = (row.allowance_type || '').includes('international');
    return {
        date,
        allowance_type: row.allowance_type,
        destination: row.destination,
        country: row.country,
        start_datetime: row.start_datetime,
        end_datetime: row.end_datetime,
        nights: row.nights,
        breakfast_provided: row.breakfast_provided,
        lunch_provided: row.lunch_provided,
        dinner_provided: row.dinner_provided,
        international_day_rate: isIntl && parseFloat(row.international_day_rate) > 0 ? parseFloat(row.international_day_rate) : null,
        international_night_rate: isIntl && parseFloat(row.international_night_rate) > 0 ? parseFloat(row.international_night_rate) : null,
        payment_method: row.payment_method,
        project_id: row.project_id || null,
        activity_id: row.activity_id || null,
        billable: row.billable,
        notes: row.notes || null,
    };
}

// `original` is the row's allowanceInitialSnapshot entry (undefined only if
// it's somehow missing, treated as "changed" — the safe default).
//
// 23.R5: activity_id is included ONLY when it actually changed from the
// snapshot — mirroring extractUpdatablePayload() in day-mileage-form.jsx and
// extractUpdatePayload() in day-receipt-form.jsx (23.R4). The backend
// re-validates the activity whenever this key is present in the payload at
// all, so unconditionally sending `row.activity_id || null` re-triggered that
// validation on every save of a row that HAS an activity, even when the edit
// touched an unrelated field. If that activity had since been deleted from
// the project, the unrelated edit was rejected — exactly the case KD-083 /
// finding 5.175 (Principle 2, "No Data Loss, Ever") was written to protect.
// Omitting the key when unchanged keeps a legacy/edited row editable; when
// the user does change (or clear) the activity, row.activity_id already
// differs from original.activity_id and gets sent (still normalised to null
// so clearing an activity is persisted as "no activity", not '').
function extractAllowanceUpdatePayload(row, original) {
    const isIntl = (row.allowance_type || '').includes('international');
    const payload = {
        allowance_type: row.allowance_type,
        destination: row.destination,
        country: row.country,
        start_datetime: row.start_datetime,
        end_datetime: row.end_datetime,
        nights: row.nights,
        breakfast_provided: row.breakfast_provided,
        lunch_provided: row.lunch_provided,
        dinner_provided: row.dinner_provided,
        // Left blank (→ null) unless the user explicitly typed a new value here —
        // the backend's PUT excludes None fields from the update, so an untouched
        // blank never wipes a previously-set custom rate we couldn't seed (see
        // note above the ALLOWANCE_ROW_FIELDS block).
        international_day_rate: isIntl && parseFloat(row.international_day_rate) > 0 ? parseFloat(row.international_day_rate) : null,
        international_night_rate: isIntl && parseFloat(row.international_night_rate) > 0 ? parseFloat(row.international_night_rate) : null,
        project_id: row.project_id || null,
        billable: row.billable,
        notes: row.notes || null,
    };
    const activityChanged = !original || String(row.activity_id ?? '') !== String(original.activity_id ?? '');
    if (activityChanged) {
        payload.activity_id = row.activity_id || null;
    }
    return payload;
}

// ── Dirty dot indicator ────────────────────────────────────────────────────────
const DirtyDot = () => (
    <span className="w-1.5 h-1.5 rounded-full bg-primary ml-1 inline-block flex-shrink-0" />
);

/**
 * UnifiedAddDialog — Tabbed version (Task 1.R.1-R)
 *
 * Single-day mode (date prop): opens a 4-tab dialog (Time / Mileage / Receipt / Stay).
 * Each tab has an independent Save button and dirty-state tracking.
 * Closing with unsaved changes prompts via window.confirm.
 *
 * Bulk mode (dates prop, non-empty) renders the exact same tabbed UI as
 * single-day mode — the only visible difference is the header ("N days
 * selected" instead of a formatted date) and that each tab's Save applies
 * to every selected date instead of one. There is no per-day "existing
 * entries" list in bulk mode (there's no single day to list); each tab
 * shows one blank add-row.
 *
 * Props:
 *   open                  {boolean}    — controlled open state
 *   onOpenChange          {function}   — called with false when dialog should close
 *   date                  {string}     — ISO date "YYYY-MM-DD"; single-day mode
 *   dayObj                {object}     — full day object from monthInfo.days (has .is_weekend, .is_red_day, .expected_hours); single-day mode only
 *   dates                 {string[]}   — array of ISO dates; bulk mode
 *   projects              {Array}      — active project list [{id, name}]
 *   deviationTypes        {Array}      — active deviation types [{id, name}]
 *   dayEntries            {Array}      — time entries; single-day mode holds the day's entries, bulk mode holds exactly one (shared) entry
 *   setDayEntries         {function}   — setState for dayEntries
 *   userBalances          {object}     — { flex, vacation, advanceVacation, advanceVacationUsed }
 *   lastProjectId         {string}     — last-used project id
 *   setLastProjectId      {function}   — persist last project selection
 *   existingExpenses      {Array}      — full month's expense docs from getMonthSummary; mileage tab filters client-side
 *   onSuccess             {function}   — called after a successful expense save (refresh calendar)
 *   onTimeSaved           {function}   — called after a successful time save (refresh calendar)
 *   initialTab            {string}     — optional: "time"|"mileage"|"receipt"|"allowance" (default "time")
 *   isTimesheetLocked     {boolean}    — when true, Time tab is rendered read-only
 *   isExpenseLocked       {boolean}    — when true, Mileage/Receipt/Allowance tabs are read-only
 */
export default function UnifiedAddDialog({
    open,
    onOpenChange,
    date,
    dayObj,
    dates,
    projects,
    deviationTypes,
    dayEntries,
    setDayEntries,
    userBalances,
    lastProjectId,
    setLastProjectId,
    existingExpenses = [],
    onSuccess,
    onTimeSaved,
    initialTab = 'time',
    initialReceiptFile = null,
    intlRateUrl = '',
    onNavigateDay,
    onDirtyChange,
    isTimesheetLocked = false,
    isExpenseLocked = false,
    weekLocked = false,
}) {
    const { t } = useTranslation();
    const isBulkMode = Array.isArray(dates) && dates.length > 0;

    // ── Single-day tab state ───────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState(initialTab || 'time');

    // Whether the receipt tab currently has a row with a document to preview —
    // reported up by DayReceiptForm (mirrors SupplierInvoicesPage's
    // hasInvoiceDocument). Only widens the dialog while ON the receipt tab, so
    // switching to another tab never leaves a stretch of empty space.
    const [receiptHasDocument, setReceiptHasDocument] = useState(false);

    // Dirty state per tab: true if user has modified inputs since last save
    const [dirty, setDirty] = useState({ time: false, mileage: false, receipt: false, allowance: false });

    const markDirty = (tab) => setDirty(prev => ({ ...prev, [tab]: true }));
    const markClean = (tab) => setDirty(prev => ({ ...prev, [tab]: false }));

    // Notify parent of aggregate dirty state when any tab changes
    useEffect(() => {
        onDirtyChange?.(Object.values(dirty).some(Boolean));
    }, [dirty, onDirtyChange]);

    // ── Expense form states ────────────────────────────────────────────────────
    // In bulk mode this is dates[0] — used only as the seed for the one blank
    // allowance row (its actual date gets overridden per-date at save time).
    const effectiveDate = isBulkMode
        ? (dates[0] || new Date().toISOString().split('T')[0])
        : (date || new Date().toISOString().split('T')[0]);

    // ── Allowance tab: multi-entry row state (single-day and bulk both use
    // this — bulk always has exactly one row, mirroring DayMileageForm /
    // DayReceiptForm's internal `rows` state) ──────────────────────────────────
    const [allowanceRows, setAllowanceRows] = useState(() => [emptyAllowanceRow(effectiveDate)]);
    const [allowanceRowsSaving, setAllowanceRowsSaving] = useState(false);
    const allowanceInitialSnapshot = useRef({});

    // Retry set for bulk allowance saves — starts as the full selection,
    // narrows to only the failed dates after a partial failure so a retry
    // doesn't resubmit (duplicate) the dates that already succeeded.
    const [allowanceBulkDates, setAllowanceBulkDates] = useState(dates || []);
    useEffect(() => {
        if (isBulkMode) setAllowanceBulkDates(dates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dates]);

    // Vehicle / receipt / allowance type options, fetched once from the
    // backend on mount so this dialog can never drift from
    // backend/config/expense_config.py.
    // null while loading — dropdowns that depend on it render a disabled
    // placeholder option instead of an empty <select> until it resolves.
    const [expenseTypes, setExpenseTypes] = useState(null);
    useEffect(() => {
        let alive = true;
        getExpenseTypes()
            .then((res) => { if (alive) setExpenseTypes(res.data); })
            .catch(() => {
                // Fetch failed — fall back to the static type list so the
                // mileage/allowance dropdowns stay usable rather than being
                // stuck disabled on a "loading" placeholder forever. No toast:
                // this is a background fetch the user didn't explicitly trigger,
                // and the fallback keeps the form fully functional.
                if (alive) setExpenseTypes(FALLBACK_EXPENSE_TYPES);
            });
        return () => { alive = false; };
    }, []);

    const resolvedIntlRateUrl = safeHttpUrl(intlRateUrl, SKV_INTL_RATE_URL);

    // Refs for sub-form save orchestration (Save Day)
    const timeRef = useRef(null);
    const mileageRef = useRef(null);
    const receiptRef = useRef(null);

    // Memoize filtered expense lists so DayMileageForm / DayReceiptForm's
    // re-seed effect (which depends on these arrays) doesn't fire every
    // render — inline .filter() returns a new reference each time.
    const mileageForDay = useMemo(
        () => (existingExpenses || []).filter(
            e => e.date === effectiveDate && e.expense_category === 'mileage'
        ),
        [existingExpenses, effectiveDate]
    );
    const receiptsForDay = useMemo(
        () => (existingExpenses || []).filter(
            e => e.date === effectiveDate && e.expense_category === 'receipt'
        ),
        [existingExpenses, effectiveDate]
    );
    const allowancesForDay = useMemo(
        () => (existingExpenses || []).filter(
            e => e.date === effectiveDate && e.expense_category === 'allowance'
        ),
        [existingExpenses, effectiveDate]
    );
    // Stable identity key for the enrichment-fetch effect below — `.length`
    // alone can stay equal across an add+delete on the same day, or between
    // two different days with equal counts, so it can miss a real change.
    // Keying off the actual ids (order-independent) catches those cases.
    const allowancesForDayIdsKey = useMemo(
        () => allowancesForDay.map(a => a.id).sort().join(','),
        [allowancesForDay]
    );

    const allowanceTypeLabel = (id) => {
        const found = (expenseTypes?.allowance_types || []).find(a => a.id === id);
        return found ? t(`expenses.allowance_type.${id}`, found.name) : id;
    };

    // ── Allowance rows: thin re-seed from month-summary (immediate paint) ─────
    useEffect(() => {
        if (isBulkMode) return;
        const seeded = allowancesForDay.map(a => seedAllowanceRow(a, effectiveDate));
        setAllowanceRows(seeded.length > 0 ? seeded : [emptyAllowanceRow(effectiveDate)]);
        allowanceInitialSnapshot.current = allowancesForDay.reduce((acc, a) => {
            acc[a.id] = seedAllowanceRow(a, effectiveDate);
            return acc;
        }, {});
        markClean('allowance');
        // Deliberately keyed on the id-set, not `allowancesForDay` itself — a
        // parent re-render can hand us a new array reference with the exact
        // same rows, which would otherwise re-seed and markClean() over a
        // user's in-progress edit. Only a real add/remove (id-set change) or
        // day change should discard local state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBulkMode, effectiveDate, allowancesForDayIdsKey]);

    // ── Allowance rows: enrichment fetch (full detail for editing) ────────────
    // GET /calendar/month-summary (the source of `allowancesForDay` above) only
    // projects a thin field set for allowances. GET /expenses/v2 already returns
    // the full record (country, start/end datetime, nights, meals) for the whole
    // month, so fetch it here and merge onto the thin-seeded rows once it lands.
    // Existing rows stay edit-disabled (see `_detailsLoaded`) until this resolves.
    useEffect(() => {
        if (isBulkMode || allowancesForDay.length === 0) return;
        let cancelled = false;
        const [yearStr, monthStr] = effectiveDate.split('-');
        getExpensesV2({ year: Number(yearStr), month: Number(monthStr), category: 'allowance' })
            .then((res) => {
                if (cancelled) return;
                const byId = new Map((res.data || []).map(e => [e.id, e]));
                setAllowanceRows(prev => prev.map(row => {
                    if (!row.id || row._detailsLoaded) return row;
                    const full = byId.get(row.id);
                    if (!full) return row;
                    const seeded = seedAllowanceRow({ ...full, _detailsLoaded: true }, effectiveDate);
                    allowanceInitialSnapshot.current[row.id] = seeded;
                    return seeded;
                }));
            })
            .catch(() => {
                // Silent — rows stay display-only (edit fieldset disabled) rather
                // than risk saving over real data with incomplete defaults.
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBulkMode, effectiveDate, allowancesForDayIdsKey]);

    // ── Allowance rows: dirty computation → feeds the shared `dirty` state ────
    const isAllowanceRowsDirty = useMemo(() => {
        for (const row of allowanceRows) {
            const isNew = !row.id;
            if (isNew && !row._markDeleted) {
                if (allowanceRowHasData(row, effectiveDate)) return true;
            } else if (!isNew) {
                if (row._markDeleted) return true;
                if (!row._detailsLoaded) continue; // can't be dirty — fields are disabled until loaded
                const orig = allowanceInitialSnapshot.current[row.id];
                if (orig && hasAllowanceRowChanged(row, orig)) return true;
            }
        }
        return false;
    }, [allowanceRows, effectiveDate]);

    useEffect(() => {
        if (isAllowanceRowsDirty) markDirty('allowance');
        else markClean('allowance');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllowanceRowsDirty]);

    // ── Allowance rows: mutation helpers ───────────────────────────────────────
    const updateAllowanceRowField = (rowIndex, field, value) => {
        setAllowanceRows(prev => prev.map((r, i) => i === rowIndex ? { ...r, [field]: value } : r));
    };

    const markAllowanceRowDeleted = (rowIndex) => {
        setAllowanceRows(prev => prev.map((r, i) => i === rowIndex ? { ...r, _markDeleted: true } : r));
    };

    const undoAllowanceRowDelete = (rowIndex) => {
        setAllowanceRows(prev => prev.map((r, i) => i === rowIndex ? { ...r, _markDeleted: false } : r));
    };

    const addAllowanceRow = () => {
        setAllowanceRows(prev => [...prev, emptyAllowanceRow(effectiveDate)]);
    };

    // ── Recent-entry auto-fill (allowance tab) ─────────────────────────────────
    // Fills type/destination/country/nights/meals/intl-rates — NOT start/end
    // datetime, which stay derived from the day being added to (Task 1.R2.5).
    // Phase 23 (PRD 12.2, KD-083 "consequence accepted at approval"):
    // GET /expenses/allowance/recent never returns project_id (it dedupes
    // purely on allowance_type + destination + country, deliberately
    // excluding project), so there is no project on the reused entry to
    // resolve an activity against either. `fill` intentionally leaves
    // project_id/activity_id/activity_name untouched: the row keeps
    // whatever the user already had selected, and if that project defines
    // activities the now-visible required field (and validateAllowanceRows
    // below) asks for one before save — exactly the accepted behaviour,
    // never a silently-carried stale activity.
    const handleSelectRecentAllowance = (entry) => {
        setAllowanceRows(prev => {
            const lastBlankIdx = prev.reduce((found, r, i) => {
                if (!r.id && !r._markDeleted) return i;
                return found;
            }, -1);
            const fill = {
                allowance_type: entry.allowance_type || 'domestic_day',
                destination: entry.destination || '',
                country: entry.country || 'Sweden',
                nights: entry.nights ?? 0,
                breakfast_provided: entry.breakfast_provided ?? 0,
                lunch_provided: entry.lunch_provided ?? 0,
                dinner_provided: entry.dinner_provided ?? 0,
                international_day_rate: entry.international_day_rate != null ? String(entry.international_day_rate) : '',
                international_night_rate: entry.international_night_rate != null ? String(entry.international_night_rate) : '',
            };
            if (lastBlankIdx >= 0) {
                return prev.map((r, i) => i === lastBlankIdx ? { ...r, ...fill } : r);
            }
            return [...prev, { ...emptyAllowanceRow(effectiveDate), ...fill }];
        });
    };

    // ── Allowance rows: shared "will Save actually look at this row" rule ─────
    // Used by validateAllowanceRows AND the inline activity-required hint in
    // the row's JSX below. Finding 5.201: the hint used to re-derive its own
    // copy of these conditions and drifted from this one, so it lit up on
    // rows Save was never going to submit (a deleted row, an untouched
    // existing row) with no way for the user to clear it. One predicate,
    // called from both places, is what keeps them from drifting apart again.
    //
    // 23.2-R: an existing row must mirror the save loop below (and the
    // fieldsLocked condition that disables its inputs) exactly, or validation
    // blocks Save Day on a row that will never be sent. A row stays
    // fields-locked — and therefore can never become dirty — until
    // _detailsLoaded is true, and even once loaded it is only ever submitted
    // if hasAllowanceRowChanged() finds a real edit. Without this, an
    // untouched legacy row (created before Activities existed, so it has no
    // activity_id) tripped the activity-required check just because some
    // *other* row on the same day was dirty.
    const isAllowanceRowCheckable = (row) => {
        if (row._markDeleted) return false;
        const isNew = !row.id;
        if (isNew) return allowanceRowHasData(row, effectiveDate); // entirely empty add-row — skipped on save
        if (!row._detailsLoaded) return false;
        const original = allowanceInitialSnapshot.current[row.id];
        return Boolean(original && hasAllowanceRowChanged(row, original));
    };

    // ── Allowance rows: validation before save ─────────────────────────────────
    const validateAllowanceRows = () => {
        for (const row of allowanceRows) {
            if (!isAllowanceRowCheckable(row)) continue;
            if (!row.project_id) {
                toast.error(t('expenses.receipt.project_required'));
                return false;
            }
            // Phase 23 (PRD 12.2): activity is required once the row's
            // project defines at least one — mirrors
            // backend/utils/activities.py's resolve_activity so the user
            // hits this toast instead of a 400 from the server.
            const selectedProject = projects?.find(p => p.id === row.project_id);
            const projectActivities = selectedProject?.activities || [];
            if (projectActivities.length > 0 && !row.activity_id) {
                toast.error(t('error.activities.required'));
                return false;
            }
            if (!row.destination?.trim()) {
                toast.error(t('expenses.allowance.purpose_required'));
                return false;
            }
            if (!row.start_datetime || !row.end_datetime) {
                toast.error(t('expenses.allowance.save_error'));
                return false;
            }
            // End must not precede start. In bulk mode the row's time-of-day is
            // re-anchored onto each selected date, so an inverted span would be
            // persisted N times over — check it once, up front.
            if (row.end_datetime < row.start_datetime) {
                toast.error(t('expenses.allowance.end_before_start_error'));
                return false;
            }
            // Nights only make sense across a multi-day span. In bulk mode every
            // record is a single day by construction, so nights > 0 there is
            // always contradictory.
            const sameDay = row.start_datetime.split('T')[0] === row.end_datetime.split('T')[0];
            if ((row.nights || 0) > 0 && (isBulkMode || sameDay)) {
                toast.error(t('expenses.allowance.nights_need_overnight_error'));
                return false;
            }
        }
        return true;
    };

    // ── Allowance rows: save handler (batched, mirrors DayMileageForm) ────────
    const handleSaveAllowanceRows = async () => {
        if (!validateAllowanceRows()) {
            throw new Error('Allowance validation failed');
        }
        setAllowanceRowsSaving(true);

        if (isBulkMode) {
            // Bulk mode: exactly one row (no existing-entries list applies
            // across N different days), fanned out to every selected date.
            // Each date gets its own independent record with a date-derived
            // start/end datetime — the row's own edited time-of-day, just
            // re-anchored onto each date (withRowDate) — NOT sent through
            // the shared POST /expenses/bulk endpoint, because that endpoint
            // only overrides `date` per iteration and would otherwise stamp
            // every created record with the same literal start/end datetime
            // regardless of which date it landed on.
            const row = allowanceRows[0];
            if (!row || !allowanceRowHasData(row, effectiveDate)) {
                setAllowanceRowsSaving(false);
                markClean('allowance');
                return;
            }
            const results = await Promise.allSettled(
                allowanceBulkDates.map(d => createAllowanceExpense(extractAllowanceCreatePayload({
                    ...row,
                    start_datetime: withRowDate(row.start_datetime, d),
                    end_datetime: withRowDate(row.end_datetime, d),
                }, d)))
            );
            const failedDates = allowanceBulkDates.filter((d, i) => results[i].status === 'rejected');
            setAllowanceRowsSaving(false);
            if (failedDates.length > 0) {
                // Narrow the retry set to only the dates that actually
                // failed — the ones that succeeded must not be resubmitted.
                setAllowanceBulkDates(failedDates);
                toast.error(t('expenses.allowance.save_error'));
                throw new Error('Allowance bulk save failed');
            }
            hapticSave();
            setAllowanceRows([emptyAllowanceRow(effectiveDate)]); // fully saved — reset so a re-save can't duplicate
            markClean('allowance');
            onSuccess?.();
            return;
        }

        // Track each op against the row it came from (by array reference) so
        // a partial failure can be reconciled per-row instead of either (a)
        // leaving succeeded creates looking "new" — re-submitted as
        // duplicates on retry — or (b) refetching everything, which would
        // wipe the failed rows' un-persisted edits before the user can retry.
        const opRows = [];
        const ops = [];
        for (const row of allowanceRows) {
            const isNew = !row.id;
            const isDeleted = row._markDeleted;

            if (isNew && isDeleted) continue;

            if (!isNew && isDeleted) {
                opRows.push(row);
                ops.push(deleteExpenseV2(row.id));
                continue;
            }

            if (isNew) {
                if (!allowanceRowHasData(row, effectiveDate)) continue; // empty add-row
                opRows.push(row);
                ops.push(createAllowanceExpense(extractAllowanceCreatePayload(row, effectiveDate)));
                continue;
            }

            // Existing row — can only be dirty once details are loaded (fields
            // stay disabled until then), so this is always safe to send in full.
            if (!row._detailsLoaded) continue;
            const original = allowanceInitialSnapshot.current[row.id];
            if (original && hasAllowanceRowChanged(row, original)) {
                opRows.push(row);
                ops.push(updateAllowanceExpense(row.id, extractAllowanceUpdatePayload(row, original)));
            }
        }

        if (ops.length === 0) {
            markClean('allowance');
            setAllowanceRowsSaving(false);
            return;
        }

        const results = await Promise.allSettled(ops);
        const failedRows = new Set(
            results.map((r, i) => (r.status === 'rejected' ? opRows[i] : null)).filter(Boolean)
        );

        // Reconcile every succeeded op into local state immediately — a
        // succeeded delete disappears, a succeeded create/update gets its
        // fresh server snapshot (so it stops looking dirty, and a create
        // stops looking "new" — no re-submitting it as a duplicate on a
        // retry). This runs the same way on full success and partial
        // failure: we don't want to depend on the parent's refetch (via
        // onSuccess) landing before the user can act again, and on partial
        // failure a refetch would actively wipe the still-failed rows'
        // un-persisted edits. Rows that failed, or weren't part of this
        // batch, are left untouched either way.
        const succeeded = new Map(); // row -> saved server record (or 'deleted')
        results.forEach((r, i) => {
            if (r.status !== 'fulfilled') return;
            const row = opRows[i];
            succeeded.set(row, row._markDeleted ? 'deleted' : r.value?.data);
        });
        setAllowanceRows(prev => {
            const next = prev
                .filter(row => succeeded.get(row) !== 'deleted')
                .map(row => {
                    const saved = succeeded.get(row);
                    if (!saved || saved === 'deleted') return row; // untouched or failed — keep as-is
                    const reseeded = seedAllowanceRow({ ...saved, _detailsLoaded: true }, effectiveDate);
                    allowanceInitialSnapshot.current[reseeded.id] = reseeded;
                    return reseeded;
                });
            return next.length > 0 ? next : [emptyAllowanceRow(effectiveDate)];
        });

        if (failedRows.size === 0) {
            hapticSave();
            toast.success(t('expenses.allowance.save_success'));
            markClean('allowance');
            onSuccess?.();
        } else {
            toast.error(t('expenses.allowance.save_error'));
            setAllowanceRowsSaving(false);
            throw new Error('Allowance save failed');
        }

        setAllowanceRowsSaving(false);
    };

    // ── Re-init on open ────────────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            const d = isBulkMode
                ? (dates[0] || new Date().toISOString().split('T')[0])
                : (date || new Date().toISOString().split('T')[0]);
            setDirty({ time: false, mileage: false, receipt: false, allowance: false });
            // Respect caller's requested tab on open
            setActiveTab(initialTab || 'time');
            // Bulk mode's allowance row isn't re-seeded by the effects below
            // (those key off allowancesForDay, which doesn't apply across N
            // dates) — reset it here so a previous bulk session's leftover
            // row/date doesn't linger into a new one (the dialog persists
            // mounted between opens, it isn't remounted per selection).
            if (isBulkMode) setAllowanceRows([emptyAllowanceRow(d)]);
        }
    }, [open, date, dates, isBulkMode, initialTab]);

    // ── Close / dirty guard ────────────────────────────────────────────────────
    const handleOpenChange = (next) => {
        if (!next) {
            const anyDirty = Object.values(dirty).some(Boolean);
            if (anyDirty) {
                const confirmed = window.confirm(t('timesheet.unsaved_changes_confirm'));
                if (!confirmed) return;
            }
        }
        onOpenChange(next);
    };


    // ── Save Day orchestrator (single-day mode) ────────────────────────────────
    const [savingDay, setSavingDay] = React.useState(false);

    const handleSaveDay = async () => {
        if (savingDay) return;
        setSavingDay(true);
        let anyFailed = false;

        try {
            if (dirty.time && timeRef.current) {
                try {
                    await timeRef.current.save();
                } catch {
                    anyFailed = true;
                }
            }

            if (dirty.mileage && mileageRef.current) {
                try {
                    await mileageRef.current.save();
                } catch {
                    anyFailed = true;
                }
            }

            if (dirty.receipt && receiptRef.current) {
                try {
                    await receiptRef.current.save();
                } catch {
                    anyFailed = true;
                }
            }

            if (dirty.allowance) {
                try {
                    await handleSaveAllowanceRows();
                } catch {
                    anyFailed = true;
                }
            }

            if (anyFailed) {
                toast.error(t('timesheet.save_day_partial'));
            } else {
                toast.success(t('timesheet.save_day_success'));
            }
        } finally {
            setSavingDay(false);
        }
    };

    // ── Date label ─────────────────────────────────────────────────────────────
    const formatDate = (d) => {
        if (!d) return '';
        return new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        });
    };

    // ── SINGLE-DAY MODE: Tabbed dialog ─────────────────────────────────────────
    const selectedDayObj = dayObj || (date ? { date, is_weekend: false, is_red_day: false } : null);

    // ── Lock banner text ────────────────────────────────────────────────────────
    // Only show the banner relevant to the active tab so that a submitted expense
    // report doesn't show a locked banner while the user is editing time entries
    // (and vice versa). When both are locked, always show the combined message.
    const lockBanner = (() => {
        if (isTimesheetLocked && isExpenseLocked) {
            return { icon: <Lock className="w-4 h-4 flex-shrink-0" />, message: t('unified_dialog.lock.all_locked') };
        }
        const onExpenseTab = activeTab === 'mileage' || activeTab === 'receipt' || activeTab === 'allowance';
        if (isTimesheetLocked && activeTab === 'time') {
            return { icon: <Lock className="w-4 h-4 flex-shrink-0" />, message: t('unified_dialog.lock.timesheet_locked') };
        }
        if (isExpenseLocked && onExpenseTab) {
            return { icon: <Lock className="w-4 h-4 flex-shrink-0" />, message: t('unified_dialog.lock.expenses_locked') };
        }
        return null;
    })();

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className={cn(
                    'max-h-[90vh] !p-0 !gap-0 flex flex-col overflow-hidden',
                    activeTab === 'receipt' && receiptHasDocument ? 'max-w-5xl' : 'max-w-2xl'
                )}
            >
                {/* Fixed header — does not scroll */}
                <div className="flex-shrink-0 px-6 pt-6 pb-3">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            <div className="flex items-center w-full pr-10">
                                {onNavigateDay && !isBulkMode ? (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="flex-shrink-0 min-w-[44px] min-h-[44px]"
                                        aria-label={t('timesheet.navigation.prev_day')}
                                        onClick={() => onNavigateDay(-1)}
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </Button>
                                ) : (
                                    <span className="flex-shrink-0 min-w-[44px]" />
                                )}
                                <span className="flex-1 text-center">
                                    {isBulkMode ? t('common.days_selected', { count: dates.length }) : formatDate(date)}
                                </span>
                                {onNavigateDay && !isBulkMode ? (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="flex-shrink-0 min-w-[44px] min-h-[44px]"
                                        aria-label={t('timesheet.navigation.next_day')}
                                        onClick={() => onNavigateDay(1)}
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </Button>
                                ) : (
                                    <span className="flex-shrink-0 min-w-[44px]" />
                                )}
                            </div>
                        </DialogTitle>
                    </DialogHeader>
                </div>

                {lockBanner && (
                    <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-200 text-xs">
                        {lockBanner.icon}
                        <span>{lockBanner.message}</span>
                    </div>
                )}

                {weekLocked && (
                    <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-200 text-xs">
                        <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{t('timesheet.week_locked_banner')}</span>
                    </div>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-shrink-0 px-6 pb-1">
                    <TabsList className="grid grid-cols-4 w-full">
                        <TabsTrigger value="time" className="flex items-center justify-center gap-1 px-1 min-w-0">
                            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate text-xs">{t('timesheet.tabs.time')}</span>
                            {dirty.time && <DirtyDot />}
                        </TabsTrigger>

                        <TabsTrigger value="mileage" className="flex items-center justify-center gap-1 px-1 min-w-0">
                            <Car className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate text-xs">{t('timesheet.tabs.miles')}</span>
                            {dirty.mileage && <DirtyDot />}
                        </TabsTrigger>

                        <TabsTrigger value="receipt" className="flex items-center justify-center gap-1 px-1 min-w-0">
                            <Receipt className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate text-xs">{t('timesheet.tabs.receipts')}</span>
                            {dirty.receipt && <DirtyDot />}
                        </TabsTrigger>

                        <TabsTrigger value="allowance" className="flex items-center justify-center gap-1 px-1 min-w-0">
                            <Hotel className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate text-xs">{t('timesheet.tabs.allowances')}</span>
                            {dirty.allowance && <DirtyDot />}
                        </TabsTrigger>
                    </TabsList>
                    </div>

                    {/* Scrollable tab content */}
                    <div className="flex-1 overflow-y-auto px-6 pb-2">

                    {/* ── Time tab ────────────────────────────────────────────── */}
                    {/*
                      forceMount keeps every tab's form mounted so its internal
                      state survives tab switches (Radix unmounts inactive
                      Content by default). Without it, switching tabs wipes
                      mileage/receipt rows the user just typed AND breaks the
                      Save Day orchestrator (which calls `someRef.current.save()`
                      — null when the tab is unmounted).
                    */}
                    <TabsContent value="time" forceMount>
                        <div className={isTimesheetLocked || weekLocked ? 'pointer-events-none opacity-60 select-none' : ''}>
                            <DayTimeEntriesForm
                                ref={timeRef}
                                selectedDay={selectedDayObj}
                                dates={isBulkMode ? dates : undefined}
                                dayEntries={dayEntries || []}
                                setDayEntries={(updater) => {
                                    markDirty('time');
                                    if (typeof updater === 'function') {
                                        setDayEntries(updater);
                                    } else {
                                        setDayEntries(updater);
                                    }
                                }}
                                projects={projects || []}
                                deviationTypes={deviationTypes || []}
                                userBalances={userBalances || { flex: 0, vacation: 0, advanceVacation: 0, advanceVacationUsed: 0 }}
                                lastProjectId={lastProjectId || ''}
                                setLastProjectId={setLastProjectId || (() => {})}
                                onSaved={() => {
                                    markClean('time');
                                    onTimeSaved && onTimeSaved();
                                }}
                            />
                        </div>
                    </TabsContent>

                    {/* ── Mileage tab ─────────────────────────────────────────── */}
                    <TabsContent value="mileage" forceMount>
                        <div className={isExpenseLocked || weekLocked ? 'pointer-events-none opacity-60 select-none' : ''}>
                            <DayMileageForm
                                ref={mileageRef}
                                selectedDay={effectiveDate}
                                dates={isBulkMode ? dates : undefined}
                                existingMileage={isBulkMode ? [] : mileageForDay}
                                projects={projects || []}
                                onSaved={() => {
                                    markClean('mileage');
                                    onSuccess?.();
                                }}
                                onDirtyChange={(d) => {
                                    if (d) markDirty('mileage');
                                    else markClean('mileage');
                                }}
                            />
                        </div>
                    </TabsContent>

                    {/* ── Receipt tab ─────────────────────────────────────────── */}
                    <TabsContent value="receipt" forceMount>
                        <div className={isExpenseLocked || weekLocked ? 'pointer-events-none opacity-60 select-none' : ''}>
                            <DayReceiptForm
                                ref={receiptRef}
                                selectedDay={effectiveDate}
                                dates={isBulkMode ? dates : undefined}
                                existingReceipts={isBulkMode ? [] : receiptsForDay}
                                projects={projects || []}
                                onSaved={() => {
                                    markClean('receipt');
                                    onSuccess?.();
                                }}
                                onDirtyChange={(d) => {
                                    if (d) markDirty('receipt');
                                    else markClean('receipt');
                                }}
                                onDocumentPreviewChange={setReceiptHasDocument}
                                initialFile={initialReceiptFile}
                            />
                        </div>
                    </TabsContent>

                    {/* ── Allowance / Stay tab ─────────────────────────────────── */}
                    <TabsContent value="allowance" forceMount>
                        <div className={isExpenseLocked || weekLocked ? 'pointer-events-none opacity-60 select-none' : ''}>
                        <div className="space-y-4 py-2">
                            <AllowanceRecentEntries onSelect={handleSelectRecentAllowance} />

                            {allowanceRows.map((row, index) => {
                                const isExisting = !!row.id;
                                const isDeleted = row._markDeleted;
                                const isIntl = (row.allowance_type || '').includes('international');
                                const fieldsLocked = isDeleted || allowanceRowsSaving || (isExisting && !row._detailsLoaded);

                                return (
                                    <div
                                        key={row.id ?? `new-${index}`}
                                        className={`border border-border p-4 space-y-4 transition-opacity ${isDeleted ? 'opacity-40' : ''}`}
                                    >
                                        {/* Row header */}
                                        <div className="flex justify-between items-center">
                                            <span className={`text-xs uppercase tracking-widest text-muted-foreground ${isDeleted ? 'line-through' : ''}`}>
                                                {isExisting ? t('expenses.allowance.entry_label', { num: index + 1 }) : t('expenses.allowance.new_entry')}
                                                {isExisting && row.amount != null && (
                                                    <span className="ml-2 font-mono tabular-nums normal-case tracking-normal text-foreground">
                                                        {Math.round(parseFloat(row.amount) || 0)} SEK
                                                    </span>
                                                )}
                                                {isExisting && (
                                                    <span className="ml-2 normal-case tracking-normal">{formatDate(effectiveDate)}</span>
                                                )}
                                            </span>
                                            <div className="flex items-center gap-1">
                                                {isExisting && !isDeleted && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => markAllowanceRowDeleted(index)}
                                                        className="min-h-[44px] min-w-[44px] p-0 text-destructive"
                                                        disabled={allowanceRowsSaving}
                                                        aria-label={t('common.delete')}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                {isExisting && isDeleted && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => undoAllowanceRowDelete(index)}
                                                        className="min-h-[44px] min-w-[44px] p-0"
                                                        disabled={allowanceRowsSaving}
                                                        aria-label={t('common.undo')}
                                                    >
                                                        <Undo2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {isExisting && !row._detailsLoaded && !isDeleted && (
                                            <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
                                        )}

                                        <fieldset disabled={fieldsLocked} className="space-y-4 border-0 p-0 m-0">
                                            <div>
                                                <Label className="label-uppercase mb-2 block">{t('expenses.allowance.type_label')}</Label>
                                                <select
                                                    value={row.allowance_type}
                                                    onChange={(e) => updateAllowanceRowField(index, 'allowance_type', e.target.value)}
                                                    className="w-full h-10 px-3 border border-input bg-background text-sm"
                                                    disabled={fieldsLocked || !expenseTypes}
                                                >
                                                    {expenseTypes
                                                        ? (expenseTypes.allowance_types || []).map(a => <option key={a.id} value={a.id}>{t(`expenses.allowance_type.${a.id}`, a.name)}</option>)
                                                        : <option value={row.allowance_type}>{t('common.loading')}</option>}
                                                </select>
                                            </div>

                                            {/* International per-country rate — the flat configured rate
                                                does not vary by destination, so let the user enter the
                                                Skatteverket normalbelopp for the country they travelled to. */}
                                            {isIntl && (
                                                <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <Label className="label-uppercase mb-2 block">{t('expenses.allowance.intl_rate_label')}</Label>
                                                            <Input
                                                                type="number" min="0" step="1"
                                                                value={row.international_day_rate}
                                                                onChange={(e) => updateAllowanceRowField(index, 'international_day_rate', e.target.value)}
                                                                placeholder={t('expenses.allowance.intl_rate_label')}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="label-uppercase mb-2 block">{t('expenses.allowance.intl_night_rate_label')}</Label>
                                                            <Input
                                                                type="number" min="0" step="1"
                                                                value={row.international_night_rate}
                                                                onChange={(e) => updateAllowanceRowField(index, 'international_night_rate', e.target.value)}
                                                                placeholder={t('expenses.allowance.intl_night_rate_label')}
                                                            />
                                                        </div>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">{t('expenses.allowance.intl_rate_hint')}</p>
                                                    <a
                                                        href={resolvedIntlRateUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                                    >
                                                        <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                                                        {t('expenses.allowance.intl_rate_link')}
                                                    </a>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <Label className="label-uppercase mb-2 block">
                                                        {t('expenses.mileage.purpose_label')} <span className="text-destructive">*</span>
                                                    </Label>
                                                    <Input
                                                        value={row.destination}
                                                        onChange={(e) => updateAllowanceRowField(index, 'destination', e.target.value)}
                                                        placeholder={t('expenses.mileage.purpose_label')}
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="label-uppercase mb-2 block">{t('expenses.allowance.country_label')}</Label>
                                                    <Input
                                                        value={row.country}
                                                        onChange={(e) => updateAllowanceRowField(index, 'country', e.target.value)}
                                                        placeholder={t('expenses.allowance.country_placeholder')}
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <Label className="label-uppercase mb-2 block">{t('vacation.dialog.start_label')}</Label>
                                                    <Input
                                                        type="datetime-local"
                                                        value={row.start_datetime}
                                                        onChange={(e) => updateAllowanceRowField(index, 'start_datetime', withRowDate(e.target.value, effectiveDate))}
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="label-uppercase mb-2 block">{t('vacation.dialog.end_label')}</Label>
                                                    <Input
                                                        type="datetime-local"
                                                        value={row.end_datetime}
                                                        onChange={(e) => updateAllowanceRowField(index, 'end_datetime', withRowDate(e.target.value, effectiveDate))}
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <Label className="label-uppercase mb-2 block">{t('expenses.allowance.nights_label')}</Label>
                                                <Input
                                                    type="number" min="0" step="1"
                                                    value={row.nights}
                                                    onChange={(e) => updateAllowanceRowField(index, 'nights', parseInt(e.target.value) || 0)}
                                                />
                                            </div>

                                            <div>
                                                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t('expense_settings.meal_title')}</p>
                                                <div className="flex gap-6">
                                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                        <Checkbox
                                                            checked={row.breakfast_provided > 0}
                                                            onCheckedChange={(c) => updateAllowanceRowField(index, 'breakfast_provided', c ? 1 : 0)}
                                                        />
                                                        {t('expense_settings.breakfast')}
                                                    </label>
                                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                        <Checkbox
                                                            checked={row.lunch_provided > 0}
                                                            onCheckedChange={(c) => updateAllowanceRowField(index, 'lunch_provided', c ? 1 : 0)}
                                                        />
                                                        {t('expense_settings.lunch')}
                                                    </label>
                                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                        <Checkbox
                                                            checked={row.dinner_provided > 0}
                                                            onCheckedChange={(c) => updateAllowanceRowField(index, 'dinner_provided', c ? 1 : 0)}
                                                        />
                                                        {t('expense_settings.dinner')}
                                                    </label>
                                                </div>
                                            </div>

                                            <div>
                                                <Label className="label-uppercase mb-2 block">
                                                    {t('expenses.receipt.project_label')} <span className="text-destructive">*</span>
                                                </Label>
                                                {projects && projects.length > 0 ? (
                                                    <select
                                                        value={row.project_id}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            // Phase 23 (PRD 12.2): changing the project re-evaluates
                                                            // whether an activity applies at all — clear a
                                                            // now-invalid selection rather than silently carry an
                                                            // activity id that belongs to the old project.
                                                            setAllowanceRows(prev => prev.map((r, i) => i === index
                                                                ? { ...r, project_id: val, activity_id: '', activity_name: '' }
                                                                : r));
                                                        }}
                                                        className="w-full h-10 px-3 border border-input bg-background text-sm"
                                                        data-testid={`allowance-project-select-${index}`}
                                                    >
                                                        <option value="">{t('expenses.receipt.project_placeholder')}</option>
                                                        {projects.map(pr => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
                                                    </select>
                                                ) : (
                                                    <p className="text-xs text-destructive">{t('expenses.receipt.no_projects_available')}</p>
                                                )}
                                            </div>

                                            {row.project_id && (() => {
                                                const selectedProject = projects?.find(pr => pr.id === row.project_id);
                                                const projectActivities = selectedProject?.activities || [];
                                                if (projectActivities.length === 0) return null;
                                                // Phase 23 (PRD 12.2): shown ONLY when the project defines
                                                // activities — a project with none renders no field at all
                                                // (matches day-time-entries-form.jsx's pattern exactly).
                                                return (
                                                    <div>
                                                        <Label className="label-uppercase mb-2 block">
                                                            {t('timesheet.bulk_dialog.activity_label')} <span className="text-destructive">*</span>
                                                        </Label>
                                                        <select
                                                            value={row.activity_id || ''}
                                                            onChange={(e) => {
                                                                const actId = e.target.value;
                                                                const act = projectActivities.find(a => a.id === actId);
                                                                setAllowanceRows(prev => prev.map((r, i) => i === index
                                                                    ? { ...r, activity_id: actId, activity_name: act ? act.name : '' }
                                                                    : r));
                                                            }}
                                                            className="w-full h-10 px-3 border border-input bg-background text-sm"
                                                            data-testid={`allowance-activity-select-${index}`}
                                                            aria-required="true"
                                                        >
                                                            <option value="">{t('timesheet.bulk_dialog.activity_placeholder')}</option>
                                                            {projectActivities.map(act => (
                                                                <option key={act.id} value={act.id}>{act.name}</option>
                                                            ))}
                                                        </select>
                                                        {/* Names the exact offending row inline (this card is already
                                                            headed "Entry {{num}}" / "New entry" above) instead of
                                                            leaving the save-time toast to name none of them. Gated by
                                                            isAllowanceRowCheckable — the same rule validateAllowanceRows
                                                            uses to decide whether this row will be looked at on Save —
                                                            so a deleted row, an empty add-row, or an untouched existing
                                                            row never shows a required-field error Save will never act on
                                                            (finding 5.201). */}
                                                        {!row.activity_id && isAllowanceRowCheckable(row) && (
                                                            <p className="text-xs text-destructive mt-1">{t('error.activities.required')}</p>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            <div>
                                                <Label className="label-uppercase mb-2 block">{t('expenses.receipt.notes_label')}</Label>
                                                <Input
                                                    value={row.notes}
                                                    onChange={(e) => updateAllowanceRowField(index, 'notes', e.target.value)}
                                                    placeholder={t('expenses.receipt.notes_placeholder')}
                                                />
                                            </div>
                                        </fieldset>

                                        {isDeleted && (
                                            <p className="text-xs text-muted-foreground">
                                                {t('expenses.receipt.delete_pending_notice')}{' '}
                                                <button
                                                    type="button"
                                                    onClick={() => undoAllowanceRowDelete(index)}
                                                    className="underline hover:no-underline inline-flex items-center min-h-[44px] px-1"
                                                    disabled={allowanceRowsSaving}
                                                >
                                                    {t('common.undo')}
                                                </button>
                                            </p>
                                        )}
                                    </div>
                                );
                            })}

                            {!isBulkMode && (
                                <Button
                                    variant="outline"
                                    className="w-full uppercase tracking-wider text-xs"
                                    onClick={addAllowanceRow}
                                    disabled={allowanceRowsSaving}
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    {t('expenses.allowance.add_another')}
                                </Button>
                            )}
                        </div>
                        </div>
                    </TabsContent>
                    </div>{/* end scrollable tab content */}
                </Tabs>

                {/* ── Save footer — same button drives both single-day and bulk ── */}
                {!weekLocked && (
                <div className="flex-shrink-0 flex justify-end pt-2 pb-4 px-6 border-t border-border mt-0">
                    <Button
                        onClick={handleSaveDay}
                        disabled={savingDay || allowanceRowsSaving || !Object.values(dirty).some(Boolean)}
                        className="w-full sm:w-auto min-h-[44px]"
                        data-testid="save-day-btn"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {savingDay
                            ? t('common.saving')
                            : isBulkMode
                                ? t('timesheet.bulk_dialog.submit_button', { count: dates.length })
                                : t('timesheet.save_day_button')}
                    </Button>
                </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
